/*
 * German-format amount parsing/formatting, shared by `AmountInput` (SMT-1358).
 *
 * German notation uses "." as the thousands separator and "," as the decimal
 * separator ("1.500,00"). A user typing on a plain text field can also end up
 * with a single "." meant as a decimal point (typing "33.33" instead of
 * "33,33"), so a dot is only treated as a thousands separator when it clearly
 * looks like one; see `parseGermanAmount` below for the exact rule.
 *
 * `parseGermanAmount` always rounds to the field's `precision` (so a
 * precision-0 field can still be typed with cents, see `isAllowedAmountText`
 * below) and always returns `0` rather than `-0`.
 */

export const parseGermanAmount = (
  text: string,
  options?: { precision?: number }
): number | null => {
  const precision = options?.precision ?? 2;

  let cleaned = text.replace(/\s/g, "").replace(/€/g, "");

  // No digit, no amount: "", "-", "." or ".." would otherwise reach `Number`
  // as an empty string and come back as 0, a value nobody typed.
  if (!/\d/.test(cleaned)) {
    return null;
  }

  // A leading minus is always parsed as negative: the input filter
  // (`isAllowedAmountText`) is what keeps a minus out of fields that don't
  // allow it, so by the time text reaches here a minus sign always means the
  // operator typed one and it should not be silently dropped (SMT-1295 - see
  // the comment on `AmountInput` for the full context).
  const isNegative = cleaned.startsWith("-");
  if (isNegative) {
    cleaned = cleaned.slice(1);
  }

  const hasComma = cleaned.includes(",");

  let normalized: string;
  if (hasComma) {
    // "," is always the decimal separator here; every "." before it is a
    // thousands separator and is simply removed ("1.500,00" -> "1500.00").
    const [integerPart, decimalPart = ""] = cleaned.split(",");
    normalized = `${integerPart.replace(/\./g, "")}.${decimalPart}`;
  } else {
    // No comma: a single "." followed by 1..precision trailing digits reads
    // as a decimal point ("33.33" -> 33.33, "4.5" -> 4.5). Anything else (no
    // dot, more trailing digits than the field's precision as in "1.500", or
    // precision 0 where a dot can never be a decimal point) is a thousands
    // separator and is removed ("1.500" -> 1500; with precision 0, "4.5" ->
    // 45).
    const singleDotDecimal =
      precision > 0 && new RegExp(`^\\d+\\.\\d{1,${precision}}$`).test(cleaned);
    normalized = singleDotDecimal ? cleaned : cleaned.replace(/\./g, "");
  }

  const numericValue = Number(normalized);

  if (Number.isNaN(numericValue)) {
    return null;
  }

  // Round to the field's precision rather than truncating: typing "12,50"
  // into a precision-0 field is a habit (operators type ",00"/",50" the way
  // they would on any amount field), and `toFixed` is what turns that into
  // the whole number ELSTER actually stores (13, not 12 or 1250).
  const rounded = Number(numericValue.toFixed(precision));
  const signedValue = isNegative ? -rounded : rounded;

  // `-0` is a valid float but not a valid answer: "-0" or "-0,00" both mean
  // zero, and displaying "-0,00" after blur would tell the operator they
  // entered a negative amount when they entered nothing of the sort.
  return signedValue === 0 ? 0 : signedValue;
};

export const formatGermanAmount = (
  value: number,
  options?: { precision?: number; useGrouping?: boolean }
): string => {
  const precision = options?.precision ?? 2;
  const useGrouping = options?.useGrouping ?? true;

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping,
  }).format(value);
};

/*
 * Input filter for `AmountInput`: whether `text` is a valid intermediate (or
 * final) state while typing a German amount. Used to reject a keystroke
 * outright rather than trying to correct it after the fact.
 *
 * Decimal digits are allowed while typing even on a precision-0 (whole-euro)
 * field: an operator types ",00" or ",50" out of habit on any amount field,
 * and rejecting the comma outright made the digits that followed it land in
 * the integer part instead ("25.000,00" -> 2500000). Letting the comma
 * through and rounding on parse (`parseGermanAmount`) is what turns that back
 * into the intended 25000.
 */
const TYPING_DECIMAL_DIGITS_AT_ZERO_PRECISION = 2;

export const isAllowedAmountText = (
  text: string,
  options?: { precision?: number; allowNegative?: boolean }
): boolean => {
  const precision = options?.precision ?? 2;
  const allowNegative = options?.allowNegative ?? false;

  const decimalDigits =
    precision === 0 ? TYPING_DECIMAL_DIGITS_AT_ZERO_PRECISION : precision;
  const signPart = allowNegative ? "-?" : "";
  const decimalPart = `(,\\d{0,${decimalDigits}})?`;
  const pattern = new RegExp(`^${signPart}[0-9.]*${decimalPart}$`);

  return pattern.test(text);
};
